import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { PlayEventParticipant } from '@court-sync/shared';
import { sortPlayEventMatchesForDisplay } from '@court-sync/shared';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import { PlayFeatureGate } from '@/components/play/PlayFeatureGate';
import { PlayEventAddParticipantDrawer } from '@/components/play/PlayEventAddParticipantDrawer';
import { PlayEventDrawDialog } from '@/components/play/PlayEventDrawDialog';
import { PlayEventDrawSetupWizard } from '@/components/play/PlayEventDrawSetupWizard';
import {
  PlayEventRosterPanel,
  type PlayEventRosterPanelHandle,
} from '@/components/play/PlayEventRosterPanel';
import { formatPlayEventRoundTableRow } from '@/components/play/playEventMatchTable';
import { PlayEventSettingsPanel } from '@/components/play/PlayEventSettingsPanel';
import { CancelPlayEventConfirmDialog } from '@/components/play/CancelPlayEventConfirmDialog';
import { DeletePlayEventConfirmDialog } from '@/components/play/DeletePlayEventConfirmDialog';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';
import {
  dashboardTableCellClass,
  dashboardTableDataRowClass,
  dashboardTableHeadClass,
  dashboardTableHeaderRowClass,
} from '@/components/dashboard/DashboardSectionCard';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { useCurrentAcademy } from '@/hooks/useDataStore';
import { clubService } from '@/services/club';
import { organizationService } from '@/services/organization';
import { playEventService, type PlayEventDetailResponse } from '@/services/playEvent';
import { buildPlayEventPublicUrl } from '@/lib/playEventBoardPaths';
import {
  PLAY_EVENT_FORMAT_LABELS,
  PLAY_EVENT_SIDE_LABELS,
  PLAY_EVENT_STATUS_BADGE_VARIANT,
  PLAY_EVENT_STATUS_LABELS,
} from '@/lib/playEventLabels';
import { ExternalLink, EyeOff, Monitor, MoreVertical, Play, Plus, RefreshCw, Trash2, Users, XCircle } from 'lucide-react';
import { isUuid } from '@/utils/uuid';

type DetailTab = 'roster' | 'matches' | 'settings';

const DETAIL_TABS: { id: DetailTab; label: string; mobileLabel: string }[] = [
  { id: 'roster', label: 'Roster', mobileLabel: 'Roster' },
  { id: 'matches', label: 'Matches', mobileLabel: 'Matches' },
  { id: 'settings', label: 'Settings', mobileLabel: 'Settings' },
];

type RosterRemovalMode = 'remove' | 'withdraw';

export default function PlayEventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const eventsListHref =
    (location.state as { returnTo?: string } | null)?.returnTo ?? '/admin/play-events';
  const { currentClub, isLoading: clubLoading } = useCurrentClub();
  const { currentAcademy } = useCurrentAcademy();
  const [activeTab, setActiveTab] = useState<DetailTab>('roster');
  const [detail, setDetail] = useState<PlayEventDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [addParticipantOpen, setAddParticipantOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
  const [drawDialogOpen, setDrawDialogOpen] = useState(false);
  const [drawSetupOpen, setDrawSetupOpen] = useState(false);
  const [drawFocusRoundNumber, setDrawFocusRoundNumber] = useState<number | null>(null);
  const [drawParticipants, setDrawParticipants] = useState<PlayEventParticipant[]>([]);
  const [drawParticipantsLoading, setDrawParticipantsLoading] = useState(false);
  const [wizardParticipants, setWizardParticipants] = useState<PlayEventParticipant[]>([]);
  const rosterPanelRef = useRef<PlayEventRosterPanelHandle>(null);
  const [courtNamesById, setCourtNamesById] = useState<Record<string, string>>({});
  const [orgSlug, setOrgSlug] = useState<string | null>(null);
  const [onlinePaymentsEnabled, setOnlinePaymentsEnabled] = useState(false);
  const eventIdIsValid = isUuid(eventId);

  const loadDetail = useCallback(async () => {
    if (!currentClub || !eventId || !eventIdIsValid) return;
    setLoading(true);
    try {
      const res = await playEventService.getEvent(currentClub.organizationId, eventId);
      if (res.success && res.data) {
        setDetail(res.data);
      } else {
        setDetail(null);
        toast.error(res.error ?? 'Failed to load event');
      }
    } catch (error) {
      setDetail(null);
      toast.error(error instanceof Error ? error.message : 'Failed to load event');
    } finally {
      setLoading(false);
    }
  }, [currentClub, eventId, eventIdIsValid]);

  useEffect(() => {
    if (!eventIdIsValid) {
      setLoading(false);
      return;
    }
    if (clubLoading) {
      return;
    }
    if (!currentClub) {
      setLoading(false);
      setDetail(null);
      return;
    }
    void loadDetail();
  }, [clubLoading, currentClub, eventIdIsValid, loadDetail]);

  useEffect(() => {
    const clubId = detail?.event?.clubId ?? currentClub?.id;
    if (!clubId) return undefined;
    void clubService.getClubCourts(clubId).then((res) => {
      if (res.success && res.data) {
        setCourtNamesById(Object.fromEntries(res.data.map((court) => [court.id, court.name])));
      }
    });
    return undefined;
  }, [currentClub, detail?.event?.clubId]);

  useEffect(() => {
    let cancelled = false;
    void organizationService.getMyOrganization().then((res) => {
      if (cancelled) return;
      if (res.success && res.data) {
        setOrgSlug(res.data.slug);
        const orgConnect = res.data.stripeConnectAccountId;
        const clubConnect = currentClub?.stripeConnectAccountId;
        setOnlinePaymentsEnabled(Boolean(orgConnect ?? clubConnect));
      } else {
        setOrgSlug(null);
        setOnlinePaymentsEnabled(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [currentClub?.id, currentClub?.stripeConnectAccountId]);

  const participantSummary = detail?.participantSummary ?? {
    total: 0,
    checkedIn: 0,
    unpaidPriced: 0,
    registeredMemberIds: [],
    registeredStudentIds: [],
  };
  const event = detail?.event;
  const isDraftEvent = event?.status === 'draft';
  const rosterRemovalMode: RosterRemovalMode = isDraftEvent ? 'remove' : 'withdraw';

  const reloadParticipantViews = useCallback(async () => {
    await loadDetail();
    rosterPanelRef.current?.reload();
  }, [loadDetail]);

  const handleMatchCourtUpdated = useCallback((matchId: string, courtId: string | null) => {
    setDetail((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        matches: previous.matches.map((match) =>
          match.id === matchId ? { ...match, courtId } : match
        ),
      };
    });
  }, []);

  const matchesByRound = useMemo(() => {
    const groups = new Map<number, NonNullable<PlayEventDetailResponse['matches']>>();
    for (const match of detail?.matches ?? []) {
      const existing = groups.get(match.roundNumber) ?? [];
      existing.push(match);
      groups.set(match.roundNumber, existing);
    }
    const roundsByNumber = new Map((detail?.rounds ?? []).map((round) => [round.roundNumber, round]));
    return [...groups.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([roundNumber, roundMatches]) => ({
        roundNumber,
        matches: sortPlayEventMatchesForDisplay(roundMatches),
        round: roundsByNumber.get(roundNumber) ?? null,
      }));
  }, [detail?.matches, detail?.rounds]);

  const openDrawDialog = (roundNumber?: number) => {
    setDrawFocusRoundNumber(roundNumber ?? null);
    setDrawParticipants(detail?.participants ?? []);
    setDrawParticipantsLoading(true);
    setDrawDialogOpen(true);
  };

  const handleDrawDialogOpenChange = (open: boolean) => {
    setDrawDialogOpen(open);
    if (!open) {
      setDrawFocusRoundNumber(null);
      setDrawParticipants([]);
      setDrawParticipantsLoading(false);
    }
  };

  useEffect(() => {
    if (!drawDialogOpen || !currentClub || !eventId) return undefined;
    let cancelled = false;
    setDrawParticipantsLoading(true);
    void playEventService
      .listParticipants(currentClub.organizationId, eventId)
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setDrawParticipants(res.data);
        } else if (!detail?.participants?.length) {
          setDrawParticipants([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDrawParticipantsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentClub, detail?.participants, drawDialogOpen, eventId]);

  const openDrawSetup = useCallback(async () => {
    if (!currentClub || !eventId) return;
    const res = await playEventService.listParticipants(currentClub.organizationId, eventId);
    if (res.success && res.data) {
      setWizardParticipants(res.data);
    } else {
      setWizardParticipants([]);
    }
    setDrawSetupOpen(true);
  }, [currentClub, eventId]);

  if (eventId === 'new') {
    return <Navigate to="/admin/play-events?create=1" replace />;
  }

  if (!eventIdIsValid) {
    return (
      <PlayFeatureGate>
        <DashboardEmptyStateCard>
          <DashboardEmptyStateIcon icon={Play} />
          <h3 className="mb-2 text-lg font-semibold">Event not found</h3>
          <Button asChild variant="outline">
            <Link to={eventsListHref}>Back to events</Link>
          </Button>
        </DashboardEmptyStateCard>
      </PlayFeatureGate>
    );
  }

  const handlePublish = async () => {
    if (!currentClub || !eventId) return;
    setActionLoading(true);
    try {
      const res = await playEventService.publishEvent(currentClub.organizationId, eventId);
      if (res.success) {
        toast.success('Event published — it is now visible on your public play events page');
        await loadDetail();
      } else {
        toast.error(res.error ?? 'Failed to publish');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnpublish = async () => {
    if (!currentClub || !eventId) return;
    setActionLoading(true);
    try {
      const res = await playEventService.unpublishEvent(currentClub.organizationId, eventId);
      if (res.success) {
        toast.success('Event unpublished — it is no longer visible on your public site');
        setUnpublishDialogOpen(false);
        await loadDetail();
      } else {
        toast.error(res.error ?? 'Failed to unpublish');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenCheckIn = async () => {
    if (!currentClub || !eventId) return;
    setActionLoading(true);
    try {
      const res = await playEventService.openCheckIn(currentClub.organizationId, eventId);
      if (res.success) {
        toast.success('Check-in opened');
        await loadDetail();
      } else {
        toast.error(res.error ?? 'Failed to open check-in');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseCheckIn = async () => {
    if (!currentClub || !eventId) return;
    setActionLoading(true);
    try {
      const res = await playEventService.closeCheckIn(currentClub.organizationId, eventId);
      if (res.success) {
        toast.success('Check-in closed');
        await loadDetail();
      } else {
        toast.error(res.error ?? 'Failed to close check-in');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerateFutureRounds = async () => {
    if (!currentClub || !eventId) return;
    setActionLoading(true);
    try {
      const res = await playEventService.regenerateFutureRounds(
        currentClub.organizationId,
        eventId
      );
      if (res.success) {
        toast.success('Future matches regenerated');
        await loadDetail();
      } else {
        toast.error(res.error ?? 'Failed to regenerate matches');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelEvent = async () => {
    if (!currentClub || !eventId) return;
    setActionLoading(true);
    try {
      const res = await playEventService.cancelEvent(currentClub.organizationId, eventId);
      if (res.success) {
        toast.success('Event cancelled');
        setCancelDialogOpen(false);
        await loadDetail();
      } else {
        toast.error(res.error ?? 'Failed to cancel event');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!currentClub || !eventId) return;
    setActionLoading(true);
    try {
      const res = await playEventService.deleteEvent(currentClub.organizationId, eventId);
      if (res.success) {
        toast.success('Event deleted');
        setDeleteDialogOpen(false);
        navigate('/admin/play-events');
      } else {
        toast.error(res.error ?? 'Failed to delete event');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const rounds = detail?.rounds ?? [];
  const checkedInCount = participantSummary.checkedIn;
  const canManageRoster =
    event != null && event.status !== 'cancelled' && event.status !== 'completed';
  const isSocialRotate = event?.format === 'social_rotate';
  const socialStarted = isSocialRotate && (rounds.length > 0 || event?.status === 'in_progress');
  const canStartSocialEvent =
    isSocialRotate &&
    !socialStarted &&
    checkedInCount >= 2 &&
    ['published', 'check_in_open'].includes(event?.status ?? '');
  const canRegenerateSocialMatches =
    isSocialRotate && socialStarted && event?.status === 'in_progress';
  const canOpenCheckIn = event?.status === 'published';
  const canCloseCheckIn = event?.status === 'check_in_open';
  const canUnpublish = event?.status === 'published';
  const canCancelEvent =
    event != null && event.status !== 'cancelled' && event.status !== 'completed';
  const canDeleteEvent = event != null && event.status !== 'in_progress';
  const publicEventUrl =
    event && orgSlug && event.status !== 'draft' && event.status !== 'cancelled'
      ? buildPlayEventPublicUrl(orgSlug, event.slug)
      : null;
  const drawSetupLabel = event
    ? isSocialRotate
      ? socialStarted
        ? 'Event started'
        : 'Set up draw'
      : 'Set up draw'
    : 'Set up draw';
  const canOpenDrawSetup = event != null && (isSocialRotate ? canStartSocialEvent : rounds.length === 0);
  const unpaidPricedCount = participantSummary.unpaidPriced;
  const showManualPaymentAlert = !onlinePaymentsEnabled && unpaidPricedCount > 0;

  return (
    <PlayFeatureGate>
      <div className="space-y-6">
        <PageHeader
          title={event?.name ?? 'Play event'}
          subtitle={
            event
              ? `${PLAY_EVENT_FORMAT_LABELS[event.format]} · ${PLAY_EVENT_SIDE_LABELS[event.side]}`
              : undefined
          }
          breadcrumbs={[
            { label: 'Play events', href: '/admin/play-events' },
            ...(event ? [{ label: event.name }] : []),
          ]}
          actions={
            event ? (
              <div className="flex flex-wrap items-center gap-2">
                {event.status === 'draft' ? (
                  <Button onClick={() => void handlePublish()} isLoading={actionLoading}>
                    Publish
                  </Button>
                ) : null}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0"
                      aria-label="Event actions"
                      disabled={actionLoading}
                    >
                      <MoreVertical className="h-4 w-4" aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[12rem]">
                    {canOpenCheckIn ? (
                      <DropdownMenuItem onSelect={() => void handleOpenCheckIn()}>
                        Open check-in
                      </DropdownMenuItem>
                    ) : null}
                    {canCloseCheckIn ? (
                      <DropdownMenuItem onSelect={() => void handleCloseCheckIn()}>
                        Close check-in
                      </DropdownMenuItem>
                    ) : null}
                    {canStartSocialEvent ? (
                      <DropdownMenuItem onSelect={() => void openDrawSetup()}>
                        <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
                        Set up draw
                      </DropdownMenuItem>
                    ) : null}
                    {!isSocialRotate && canOpenDrawSetup ? (
                      <DropdownMenuItem onSelect={() => void openDrawSetup()}>
                        <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
                        {drawSetupLabel}
                      </DropdownMenuItem>
                    ) : null}
                    {canOpenCheckIn || canCloseCheckIn || canStartSocialEvent || !isSocialRotate ? (
                      <DropdownMenuSeparator />
                    ) : null}
                    <DropdownMenuItem asChild>
                      <Link
                        to={`/admin/play-events/${eventId}/kiosk`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Monitor className="mr-2 h-4 w-4" aria-hidden />
                        Kiosk
                        <ExternalLink className="ml-auto h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link
                        to={`/admin/play-events/${eventId}/board`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Monitor className="mr-2 h-4 w-4" aria-hidden />
                        Public board
                        <ExternalLink className="ml-auto h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                      </Link>
                    </DropdownMenuItem>
                    {publicEventUrl ? (
                      <DropdownMenuItem asChild>
                        <a href={publicEventUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="mr-2 h-4 w-4" aria-hidden />
                          Public page
                        </a>
                      </DropdownMenuItem>
                    ) : null}
                    {canUnpublish || canCancelEvent || canDeleteEvent ? <DropdownMenuSeparator /> : null}
                    {canUnpublish ? (
                      <DropdownMenuItem
                        onSelect={() => setUnpublishDialogOpen(true)}
                        className="text-destructive focus:text-destructive"
                      >
                        <EyeOff className="mr-2 h-4 w-4" aria-hidden />
                        Unpublish
                      </DropdownMenuItem>
                    ) : null}
                    {canCancelEvent ? (
                      <DropdownMenuItem
                        onSelect={() => setCancelDialogOpen(true)}
                        className="text-destructive focus:text-destructive"
                      >
                        <XCircle className="mr-2 h-4 w-4" aria-hidden />
                        Cancel event
                      </DropdownMenuItem>
                    ) : null}
                    {canDeleteEvent ? (
                      <DropdownMenuItem
                        onSelect={() => setDeleteDialogOpen(true)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" aria-hidden />
                        Delete event
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : undefined
          }
        />

        <CancelPlayEventConfirmDialog
          open={cancelDialogOpen}
          onOpenChange={(open) => !actionLoading && setCancelDialogOpen(open)}
          loading={actionLoading}
          onConfirm={handleCancelEvent}
          eventName={event?.name}
          isInProgress={event?.status === 'in_progress'}
        />

        <DeletePlayEventConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={(open) => !actionLoading && setDeleteDialogOpen(open)}
          loading={actionLoading}
          onConfirm={handleDeleteEvent}
          eventName={event?.name}
        />

        <AlertDialog
          open={unpublishDialogOpen}
          onOpenChange={(open) => !actionLoading && setUnpublishDialogOpen(open)}
        >
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>Unpublish this event?</AlertDialogTitle>
              <AlertDialogDescription>
                The event will return to draft and disappear from your public play events page.
                Court holds are released until you publish again. Registrations on the roster are
                kept.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel type="button" disabled={actionLoading}>
                Keep published
              </AlertDialogCancel>
              <Button
                type="button"
                variant="destructive"
                disabled={actionLoading}
                onClick={() => void handleUnpublish()}
              >
                Unpublish
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {event ? (
          <div className="flex flex-wrap items-center gap-2 px-1">
            <Badge variant={PLAY_EVENT_STATUS_BADGE_VARIANT[event.status]}>
              {PLAY_EVENT_STATUS_LABELS[event.status]}
            </Badge>
            <span className="text-sm text-muted-foreground">
              {format(
                event.startsAt instanceof Date ? event.startsAt : new Date(event.startsAt),
                'EEEE d MMM yyyy · HH:mm'
              )}
            </span>
          </div>
        ) : null}

        <DashboardTabNav<DetailTab>
          tabs={DETAIL_TABS}
          activeTab={activeTab}
          onChange={setActiveTab}
          ariaLabel="Play event sections"
        />

        {loading ? (
          <div className="flex min-h-[200px] items-center justify-center" aria-busy="true">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
          </div>
        ) : !detail || !event ? (
          <DashboardEmptyStateCard>
            <DashboardEmptyStateIcon icon={Play} />
            <h3 className="mb-2 text-lg font-semibold">Event not found</h3>
            <Button asChild variant="outline">
              <Link to={eventsListHref}>Back to events</Link>
            </Button>
          </DashboardEmptyStateCard>
        ) : (
          <>
            {activeTab === 'roster' ? (
              participantSummary.total === 0 ? (
                <DashboardEmptyStateCard>
                  <DashboardEmptyStateIcon icon={Users} />
                  <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                    No participants yet
                  </h3>
                  <p className="mx-auto mb-6 max-w-md text-gray-500 dark:text-gray-400">
                    Add club members, academy students, or guests from the roster. Players can also
                    self-register once the event is published.
                  </p>
                  {canManageRoster ? (
                    <Button type="button" onClick={() => setAddParticipantOpen(true)}>
                      <Plus className="mr-2 h-4 w-4" aria-hidden />
                      Add participant
                    </Button>
                  ) : null}
                </DashboardEmptyStateCard>
              ) : currentClub && eventId ? (
                <PlayEventRosterPanel
                  ref={rosterPanelRef}
                  organizationId={currentClub.organizationId}
                  eventId={eventId}
                  event={event}
                  canManageRoster={canManageRoster}
                  rosterRemovalMode={rosterRemovalMode}
                  showManualPaymentAlert={showManualPaymentAlert}
                  onAddParticipant={() => setAddParticipantOpen(true)}
                  onParticipantsChanged={() => void reloadParticipantViews()}
                />
              ) : null
            ) : null}

            {activeTab === 'matches' ? (
              <DashboardDataCard
                title="Matches & scores"
                description={
                  detail.matches.length > 0
                    ? 'Select a round to view all court matchups and teams.'
                    : undefined
                }
                headerAction={
                  canRegenerateSocialMatches ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => void handleRegenerateFutureRounds()}
                      isLoading={actionLoading}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
                      Regenerate matches
                    </Button>
                  ) : undefined
                }
              >
                {detail.matches.length === 0 ? (
                  <DashboardEmptyStateCard>
                    <DashboardEmptyStateIcon icon={Play} />
                    <p className="mb-4 text-muted-foreground">
                      {isSocialRotate
                        ? 'Set up the draw to choose rounds, pairing rules, and preview matchups before starting.'
                        : 'Set up the draw to choose allocation mode, preview matchups, and generate the schedule.'}
                    </p>
                    <Button
                      onClick={() => void openDrawSetup()}
                      disabled={!canOpenDrawSetup}
                    >
                      {drawSetupLabel}
                    </Button>
                  </DashboardEmptyStateCard>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className={dashboardTableHeaderRowClass}>
                        <TableHead className={dashboardTableHeadClass}>Round</TableHead>
                        <TableHead className={dashboardTableHeadClass}>Result</TableHead>
                        {event.matchScoringEnabled ? (
                          <TableHead className={dashboardTableHeadClass}>Score</TableHead>
                        ) : null}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {matchesByRound.map(({ roundNumber, matches: roundMatches, round }) => {
                        const startsAtLabel = round?.startsAt
                          ? format(
                              round.startsAt instanceof Date
                                ? round.startsAt
                                : new Date(round.startsAt),
                              'EEE d MMM · HH:mm'
                            )
                          : null;
                        const { primary, secondary, resultLabel, scoreLabel } =
                          formatPlayEventRoundTableRow(
                            roundNumber,
                            roundMatches,
                            round,
                            startsAtLabel
                          );
                        return (
                          <TableRow
                            key={roundNumber}
                            className={`${dashboardTableDataRowClass} cursor-pointer hover:bg-muted/50`}
                            tabIndex={0}
                            role="button"
                            onClick={() => openDrawDialog(roundNumber)}
                            onKeyDown={(keyboardEvent) => {
                              if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
                                keyboardEvent.preventDefault();
                                openDrawDialog(roundNumber);
                              }
                            }}
                          >
                            <TableCell className={dashboardTableCellClass}>
                              <span className="block font-medium">{primary}</span>
                              <span className="block text-xs text-muted-foreground">{secondary}</span>
                            </TableCell>
                            <TableCell className={dashboardTableCellClass}>{resultLabel}</TableCell>
                            {event.matchScoringEnabled ? (
                              <TableCell className={dashboardTableCellClass}>{scoreLabel}</TableCell>
                            ) : null}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </DashboardDataCard>
            ) : null}

            {activeTab === 'settings' && currentClub ? (
              <PlayEventSettingsPanel
                organizationId={currentClub.organizationId}
                clubId={currentClub.id}
                event={event}
                onUpdated={loadDetail}
              />
            ) : null}
          </>
        )}
        {event && currentClub && eventId ? (
          <PlayEventDrawSetupWizard
            open={drawSetupOpen}
            onOpenChange={setDrawSetupOpen}
            organizationId={currentClub.organizationId}
            eventId={eventId}
            event={event}
            participants={wizardParticipants}
            onCompleted={() => void loadDetail()}
          />
        ) : null}
        {event && detail ? (
          <PlayEventDrawDialog
            open={drawDialogOpen}
            onOpenChange={handleDrawDialogOpenChange}
            organizationId={currentClub.organizationId}
            event={event}
            rounds={detail.rounds}
            matches={detail.matches}
            participants={drawParticipants}
            teams={detail.teams}
            courtNamesById={courtNamesById}
            initialRoundNumber={drawFocusRoundNumber}
            participantsLoading={drawParticipantsLoading}
            rosterParticipants={drawParticipants.filter((p) => p.status === 'checked_in')}
            onParticipantUpdated={() => void reloadParticipantViews()}
            onMatchCourtUpdated={handleMatchCourtUpdated}
          />
        ) : null}
        {currentClub && event ? (
          <PlayEventAddParticipantDrawer
            organizationId={currentClub.organizationId}
            clubId={currentClub.id}
            academyId={currentAcademy?.id}
            event={event}
            registeredMemberIds={participantSummary.registeredMemberIds}
            registeredStudentIds={participantSummary.registeredStudentIds}
            isOpen={addParticipantOpen}
            onClose={() => setAddParticipantOpen(false)}
            onAdded={() => void reloadParticipantViews()}
          />
        ) : null}
      </div>
    </PlayFeatureGate>
  );
}
